/**
 * _example — REFERENČNÍ UI stránka (viz modules/_example/README.md).
 *
 * Jediný účel: ukázat SPRÁVNOU architekturu volání z dashboardu a zároveň
 * demonstrovat standardní ACM UI komponenty.
 *
 *   ✅ api.mcp('example.list', {})   — MCP proxy (POST /mcp), jediná povolená cesta
 *   ❌ api.get('/apiext/_example/…') — přímý REST fetch, ZAKÁZÁNO pro nový kód
 *
 * `_example` má v module.json `kind: "reference"` — loader ho za běhu
 * nenačítá (viz src/modules/loader.ts), takže volání toolů níže vždy vrátí
 * "Tool not found". To je OČEKÁVANÉ — tahle stránka je zdrojový kód ke
 * zkopírování (`cp -r modules/_example modules/muj-modul`), ne živá funkce.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '../../lib/api';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  DataTable,
  EmptyState,
  PageHeader,
  StatCard,
  StatsGrid,
  StatusDot,
  Tabs,
  Tab,
  TabList,
  TabPanel,
} from '../../components/ui';
import { GraduationCap, Info, Layers, Loader2, Play, Shield } from 'lucide-react';

interface ExampleInstance {
  name: string;
  status: string;
}

interface ExampleListResult {
  ok: boolean;
  instances?: ExampleInstance[];
  error?: string;
}

export default function ExamplePage() {
  const { t } = useTranslation();
  const [toggleOn, setToggleOn] = useState(false);

  // ✅ Správně: MCP proxy, ne api.get('/apiext/_example/instances').
  const { data: listResult, isLoading: loadingList, error: listError } = useQuery({
    queryKey: ['_example-list'],
    queryFn: () => api.mcp<ExampleListResult>('example.list', {}),
    retry: false,
  });

  const runDryAction = (action: string) => {
    toast.info(t('example.dryRun.toast', { action }));
  };

  return (
    <div className='space-y-5'>
      <PageHeader
        icon={<GraduationCap size={20} />}
        title={t('example.title')}
        description={t('example.description')}
      />

      <Alert variant='info'>
        <Info size={16} className='mr-2 inline-block align-text-bottom' />
        <AlertTitle>{t('example.alert.title')}</AlertTitle>
        <AlertDescription>{t('example.alert.description')}</AlertDescription>
      </Alert>

      <StatsGrid columns={4}>
        <StatCard
          label={t('example.stats.listCalls')}
          value={listError ? '0' : '1'}
          sub={t('example.stats.viaMcp')}
          icon={Layers}
          variant='default'
        />
        <StatCard
          label={t('example.stats.dryActions')}
          value='∞'
          sub={t('example.stats.noSideEffects')}
          icon={Play}
          variant='warning'
        />
        <StatCard
          label={t('example.stats.kind')}
          value='reference'
          sub={t('example.stats.notLoaded')}
          icon={Shield}
          variant='success'
        />
        <StatCard
          label={t('example.stats.components')}
          value='8+'
          sub={t('example.stats.demo')}
          icon={Info}
          variant='info'
        />
      </StatsGrid>

      <Tabs defaultTab='overview'>
        <TabList>
          <Tab id='overview' icon={<Layers size={14} />}>
            {t('example.tabs.overview')}
          </Tab>
          <Tab id='dryrun' icon={<Play size={14} />}>
            {t('example.tabs.dryRun')}
          </Tab>
        </TabList>

        <TabPanel id='overview'>
          <Card>
            <CardHeader>
              <CardTitle>{t('example.list.title')}</CardTitle>
            </CardHeader>
            <CardContent>
              {loadingList && (
                <div className='flex items-center gap-2 text-sm text-gray-500'>
                  <Loader2 size={14} className='animate-spin' /> {t('common.loading')}
                </div>
              )}
              {listError && (
                <EmptyState
                  icon={<GraduationCap size={28} />}
                  title={t('example.list.expectedError')}
                  description={t('example.list.expectedErrorDesc')}
                />
              )}
              {!loadingList && !listError && listResult?.instances && listResult.instances.length > 0 && (
                <DataTable
                  compact
                  data={listResult.instances}
                  rowKey={(i) => i.name}
                  columns={[
                    { key: 'name', label: t('example.table.name') },
                    {
                      key: 'status',
                      label: t('example.table.status'),
                      render: (i) => <StatusDot status={i.status} withLabel />,
                    },
                  ]}
                />
              )}
              {!loadingList && !listError && listResult?.instances?.length === 0 && (
                <EmptyState icon={<GraduationCap size={28} />} title={t('example.empty.title')} />
              )}
            </CardContent>
          </Card>
        </TabPanel>

        <TabPanel id='dryrun'>
          <Card>
            <CardHeader>
              <CardTitle>{t('example.dryRun.title')}</CardTitle>
            </CardHeader>
            <CardContent className='space-y-6'>
              <p className='text-sm text-gray-400'>{t('example.dryRun.description')}</p>

              <div className='flex flex-wrap items-center gap-3'>
                <Button variant='primary' onClick={() => runDryAction('primary')}>
                  Primary
                </Button>
                <Button variant='secondary' onClick={() => runDryAction('secondary')}>
                  Secondary
                </Button>
                <Button variant='ghost' onClick={() => runDryAction('ghost')}>
                  Ghost
                </Button>
                <Button variant='danger' onClick={() => runDryAction('danger')}>
                  Danger
                </Button>
              </div>

              <div className='flex flex-wrap items-center gap-3'>
                <Badge variant='green'>Success</Badge>
                <Badge variant='red'>Error</Badge>
                <Badge variant='yellow'>Warning</Badge>
                <Badge variant='blue'>Info</Badge>
                <Badge variant='gray'>Default</Badge>
              </div>

              <div className='flex flex-wrap items-center gap-4'>
                <StatusDot status='running' withLabel />
                <StatusDot status='stopped' withLabel />
                <StatusDot status='building' withLabel />
                <StatusDot status='error' withLabel />
              </div>

              <button
                onClick={() => setToggleOn((v) => !v)}
                className={`flex items-center gap-2 text-sm transition-colors ${
                  toggleOn ? 'text-blue-400' : 'text-gray-500'
                }`}
              >
                Toggle ({toggleOn ? 'ON' : 'OFF'})
              </button>
            </CardContent>
          </Card>
        </TabPanel>
      </Tabs>
    </div>
  );
}
